import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import {
	EditTrainingCourseFormValues,
	TrainingCourseRequest,
} from "@/features/training/model/Training/TrainingCourse";
import { TrainingDrillRequest } from "@/features/training/model/Training/TrainingDrill";
import { TrainingDrillFolderRequest } from "@/features/training/model/Training/TrainingDrillFolder";
import {
	CreateTrainingDrillSessionRequest,
	UpdateTrainingDrillSessionRequest,
} from "@/features/training/model/Training/TrainingDrillSession";
import { TrainingFileRequest } from "@/features/training/model/Training/TrainingFile";
import { TrainingFolderRequest } from "@/features/training/model/Training/TrainingFolder";
import { TrainingLinkRequest } from "@/features/training/model/Training/TrainingLink";
import {
	CompleteTrainingSessionRequest,
	TrainingSessionRequest,
} from "@/features/training/model/Training/TrainingSession";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { TrainingQueryKeys } from "@/service/TrainingQueries";

export const useMutateUpdateTrainingDrillFolder = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: updateTrainingDrillFolder } = useMutation({
		mutationFn: ({
			request,
			folderId,
		}: {
			request: TrainingDrillFolderRequest;
			folderId: number;
		}) => {
			return SATOSPortal.UpdateTrainingDrillFolder(request, folderId);
		},
		onSuccess: (_, variables) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.drillFolders],
			});
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.drillFolders, variables.folderId],
			});
		},
	});

	return updateTrainingDrillFolder;
};

export const useMutateDeleteTrainingDrillFolder = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: deleteTrainingDrillFolder } = useMutation({
		mutationFn: (folderId: number) => {
			return SATOSPortal.DeleteTrainingDrillFolder(folderId);
		},
		onSuccess: (_, variables) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.drillFolders],
			});
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.drillFolders, variables],
			});
		},
	});

	return deleteTrainingDrillFolder;
};

export const useMutateCreateTrainingFolder = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: createTrainingFolder } = useMutation({
		mutationFn: (request: TrainingFolderRequest) => {
			return SATOSPortal.CreateTrainingFolder(request);
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.courseFolders],
			});
		},
	});

	return createTrainingFolder;
};

export const useMutateUpdateTrainingFolder = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: updateTrainingFolder } = useMutation({
		mutationFn: ({
			request,
			folderId,
		}: {
			request: TrainingFolderRequest;
			folderId: number;
		}) => {
			return SATOSPortal.UpdateTrainingFolder(request, folderId);
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.courseFolders],
			});
		},
	});

	return updateTrainingFolder;
};

export const useMutateDeleteTrainingFolder = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: deleteTrainingFolder } = useMutation({
		mutationFn: (folderId: number) => {
			return SATOSPortal.DeleteTrainingFolder(folderId);
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.courseFolders],
			});
		},
	});

	return deleteTrainingFolder;
};

export const useMutateCreateTrainingLink = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: createTrainingLink } = useMutation({
		mutationFn: (request: TrainingLinkRequest) => {
			return SATOSPortal.CreateTrainingLink(request);
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.courseFolders],
			});
		},
	});

	return createTrainingLink;
};

export const useMutateUpdateTrainingLink = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: updateTrainingLink } = useMutation({
		mutationFn: ({
			request,
			linkId,
		}: {
			request: TrainingLinkRequest;
			linkId: number;
		}) => {
			return SATOSPortal.UpdateTrainingLink(request, linkId);
		},
		onSuccess: (_, variables) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.courseFolders],
			});
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.links, variables.linkId],
			});
		},
	});

	return updateTrainingLink;
};

export const useMutateDeleteTrainingLink = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: deleteTrainingLink } = useMutation({
		mutationFn: (linkId: number) => {
			return SATOSPortal.DeleteTrainingLink(linkId);
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.courseFolders],
			});
		},
	});

	return deleteTrainingLink;
};

export const useMutateCreateTrainingCourse = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: createTrainingCourse } = useMutation({
		mutationFn: (request: TrainingCourseRequest) => {
			return SATOSPortal.CreateTrainingCourse(request);
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.courseFolders],
			});
		},
	});

	return createTrainingCourse;
};

export const useMutateUpdateTrainingCourse = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: updateTrainingCourse } = useMutation({
		mutationFn: (values: EditTrainingCourseFormValues) => {
			return SATOSPortal.UpdateTrainingCourse(values.request, values.courseId);
		},
		onSuccess: (_, variables) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.courseFolders],
			});
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.courses, variables.courseId],
			});
		},
	});

	return updateTrainingCourse;
};

export const useMutateDeleteTrainingCourse = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: deleteTrainingCourse } = useMutation({
		mutationFn: (courseId: number) => {
			return SATOSPortal.DeleteTrainingCourse(courseId);
		},
		onSuccess: (_, courseId) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.courseFolders],
			});
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.courses, courseId],
			});
		},
	});

	return deleteTrainingCourse;
};

export const useMutateDeleteTrainingCourseFile = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: deleteTrainingCourseFile } = useMutation({
		mutationFn: ({
			courseId,
			fileId,
		}: {
			courseId: number;
			fileId: number;
		}) => {
			return SATOSPortal.DeleteTrainingCourseFile(courseId, fileId);
		},
		onSuccess: (_, variables) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.courses, variables.courseId],
			});
		},
	});

	return deleteTrainingCourseFile;
};

export const useMutateCreateTrainingDrill = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: createTrainingDrill } = useMutation({
		mutationFn: (request: TrainingDrillRequest) => {
			return SATOSPortal.CreateTrainingDrill(request);
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.drillFolders],
			});
		},
	});

	return createTrainingDrill;
};

export const useMutateUpdateTrainingDrill = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: updateTrainingDrill } = useMutation({
		mutationFn: ({
			drillId,
			request,
		}: {
			drillId: number;
			request: TrainingDrillRequest;
		}) => {
			return SATOSPortal.UpdateTrainingDrill(drillId, request);
		},
		onSuccess: (_, variables) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.drillFolders],
			});
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.drills, variables.drillId],
			});
		},
	});

	return updateTrainingDrill;
};

export const useMutateDeleteTrainingDrill = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: deleteTrainingDrill } = useMutation({
		mutationFn: (drillId: number) => {
			return SATOSPortal.DeleteTrainingDrill(drillId);
		},
		onSuccess: (_, drillId) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.drillFolders],
			});
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.drills, drillId],
			});
		},
	});

	return deleteTrainingDrill;
};

export const useMutateCreateTrainingFile = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: createTrainingFile, isPending: isSubmitting } =
		useMutation({
			mutationFn: (request: TrainingFileRequest) => {
				return SATOSPortal.CreateTrainingFile(request);
			},
			onSuccess: () => {
				void queryClient.invalidateQueries({
					queryKey: [TrainingQueryKeys.files],
				});
			},
		});

	return { createTrainingFile, isSubmitting };
};

export const useMutateDeleteTrainingDrillFile = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: deleteTrainingDrillFile } = useMutation({
		mutationFn: ({ drillId, fileId }: { drillId: number; fileId: number }) => {
			return SATOSPortal.DeleteTrainingDrillFile(drillId, fileId);
		},
		onSuccess: (_, variables) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.drills, variables.drillId],
			});
		},
	});

	return deleteTrainingDrillFile;
};

export const useMutateCreateDrillTrainingSession = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: createTrainingDrillSession } = useMutation({
		mutationFn: (request: CreateTrainingDrillSessionRequest) => {
			return SATOSPortal.CreateTrainingDrillSession(request);
		},
		onSuccess: (_, request) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.drillSessions],
			});
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.drills, request.DrillId],
			});
		},
	});

	return createTrainingDrillSession;
};

export const useMutateCreateTrainingSession = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: createTrainingSession } = useMutation({
		mutationFn: (request: TrainingSessionRequest) => {
			return SATOSPortal.CreateTrainingSession(request);
		},
		onSuccess: (_, request) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.courseSessions],
			});
			void queryClient.invalidateQueries({
				queryKey: [
					TrainingQueryKeys.courses,
					request.CourseId,
					TrainingQueryKeys.activeCourseSessions,
				],
			});
		},
	});

	return createTrainingSession;
};

export const useMutateUpdateTrainingDrillSession = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: updateTrainingDrillSession, isPending: isSubmitting } =
		useMutation({
			mutationFn: (request: UpdateTrainingDrillSessionRequest) => {
				return SATOSPortal.UpdateTrainingDrillSession(request);
			},
			onSuccess: (_, request) => {
				void queryClient.invalidateQueries({
					queryKey: [TrainingQueryKeys.drillSessions],
				});
				void queryClient.invalidateQueries({
					queryKey: [TrainingQueryKeys.drillSessions, request.Id],
				});
			},
		});

	return { updateTrainingDrillSession, isSubmitting };
};

export const useMutateUpdateTrainingSession = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: updateTrainingSession } = useMutation({
		mutationFn: ({
			sessionId,
			request,
		}: {
			sessionId: number;
			request: TrainingSessionRequest;
		}) => {
			return SATOSPortal.UpdateTrainingSession(sessionId, request);
		},
		onSuccess: (_, variables) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.courseSessions, variables.sessionId],
			});
		},
	});

	return updateTrainingSession;
};

export const useMutateDeleteTrainingDrillSession = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: deleteTrainingDrillSession } = useMutation({
		mutationFn: ({ sessionId }: { sessionId: number; drillId: number }) => {
			return SATOSPortal.DeleteTrainingDrillSession(sessionId);
		},
		onSuccess: (_, variables) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.drillSessions, variables.sessionId],
			});
			void queryClient.invalidateQueries({
				queryKey: [
					TrainingQueryKeys.drills,
					variables.drillId,
					TrainingQueryKeys.activeDrillSessions,
				],
			});
			void queryClient.invalidateQueries({
				queryKey: [
					TrainingQueryKeys.drills,
					variables.drillId,
					TrainingQueryKeys.archivedDrillSessions,
				],
			});
		},
	});

	return deleteTrainingDrillSession;
};

export const useMutateDeleteTrainingSession = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: deleteTrainingSession } = useMutation({
		mutationFn: (sessionId: number) => {
			return SATOSPortal.DeleteTrainingSession(sessionId);
		},
		onSuccess: (_, sessionId) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.courseSessions, sessionId],
			});
		},
	});

	return deleteTrainingSession;
};

export const useMutateCompleteTrainingDrillSession = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: completeTrainingDrillSession } = useMutation({
		mutationFn: (sessionId: number) => {
			return SATOSPortal.CompleteTrainingDrillSession(sessionId);
		},
		onSuccess: (_, sessionId) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.drillSessions, sessionId],
			});
		},
	});

	return completeTrainingDrillSession;
};

export const useMutateCompleteTrainingSession = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: completeTrainingSession } = useMutation({
		mutationFn: ({
			sessionId,
			request,
		}: {
			sessionId: number;
			request: CompleteTrainingSessionRequest;
		}) => {
			return SATOSPortal.CompleteTrainingSession(sessionId, request);
		},
		onSuccess: (_, variables) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.courseSessions, variables.sessionId],
			});
		},
	});

	return completeTrainingSession;
};

export const useMutateArchiveTrainingDrillSession = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: archiveTrainingDrillSession } = useMutation({
		mutationFn: (sessionId: number) => {
			return SATOSPortal.ArchiveTrainingDrillSession(sessionId);
		},
		onSuccess: (_, sessionId) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.drillSessions, sessionId],
			});
		},
	});

	return archiveTrainingDrillSession;
};

export const useMutateArchiveTrainingSession = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: archiveTrainingSession } = useMutation({
		mutationFn: (sessionId: number) => {
			return SATOSPortal.ArchiveTrainingSession(sessionId);
		},
		onSuccess: (_, sessionId) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.courseSessions, sessionId],
			});
		},
	});

	return archiveTrainingSession;
};

export const useMutateDeleteTrainingSessionStudent = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: deleteTrainingSessionStudent } = useMutation({
		mutationFn: ({
			sessionId,
			studentId,
		}: {
			sessionId: number;
			studentId: number;
		}) => {
			return SATOSPortal.DeleteTrainingSessionStudent(sessionId, studentId);
		},
		onSuccess: (_, variables) => {
			void queryClient.invalidateQueries({
				queryKey: [TrainingQueryKeys.courseSessions, variables.sessionId],
			});
		},
	});

	return deleteTrainingSessionStudent;
};
